<!--
 * @FileDescription: 创建、编辑、复制优惠券
 * @Author: 陈文景
 * @Date: 2023-03-09 15:43:37
 * @LastEditors: 最后更新作者
 * @LastEditTime: 最后更新时间
-->
<template>
  <div class="coupon-info">
    <el-form ref="form" :model="form" :rules="rules" :disabled="type === 2" size="medium" :label-width="`${labelWidth}px`">
      <Skeleton title="基本设置">
        <el-form-item label="优惠券ID">
          <span class="coupon-id">{{ type === 3 ? form.id : '自动生成' }}</span>
        </el-form-item>

        <el-form-item prop="couponTicketName" label="优惠券名称">
          <el-input v-model="form.couponTicketName" type="text" maxlength="25" show-word-limit placeholder="如: christmas gift" />
        </el-form-item>

        <el-form-item prop="description" label="优惠券描述">
          <el-input v-model="form.description" type="text" maxlength="50" show-word-limit placeholder="仅内部可见" />
        </el-form-item>

        <el-form-item prop="logoUrl" label="优惠券图片">
          <div class="coupon-uploader">
            <el-upload v-loading="uploadImgLoading" :disabled="loading" action="#" :show-file-list="false" :multiple="false" :before-upload="beforeCouponImgUpload">
              <span v-if="form.logoUrl" class="coupon-img">
                <img :src="form.logoUrl" />
                <span v-if="type !== 2" class="delete-icon" @click.stop="handleRemoveCouponImgIcon">
                  <i class="el-icon-delete" />
                </span>
              </span>
              <span v-else class="coupon-uploader-tips">
                <i class="el-icon-plus" />
                <span class="text">上传照片</span>
              </span>
            </el-upload>
            <span class="size">建议尺寸：上传1:1比例即可（例如：140x140）。</span>
            <el-popover v-if="form.logoUrl" placement="bottom-end" trigger="click" class="popper">
              <CouponItem :coupon="coupon" />
              <span slot="reference" class="size preview">预览优惠券</span>
            </el-popover>
          </div>
        </el-form-item>

        <!-- 
          关于编辑时：
          1.如已是勾选不限数量，则禁用勾选不限数量的复选框，以及，禁用输入数量的数字输入框；
          2.如未勾选不限数量的复选框，则数字输入框中的值不能小于当前优惠券已存在的值，如此时勾选不限数量的复选框，后又取消勾线，则数字输入框依然是不能小于当前优惠券已存在的值。
         -->
        <el-form-item label="发放总量" required>
          <div class="total-amount">
            <el-form-item prop="distributionRules.distributionQuantity" :show-message="!form.distributionRules.isLimitQuantity" :rules="[{ required: !form.distributionRules.isLimitQuantity, message: '请输入发放总量！', trigger: ['blur', 'change'] }]" class="form-item">
              <el-input-number v-model="form.distributionRules.distributionQuantity" :disabled="form.distributionRules.isLimitQuantity" :min="setDistributionQuantity" :max="10000" controls-position="right" placeholder="请输入发放总量" />
              <p class="tips margin-top">修改优惠券总量时只能增加不能减少，请谨慎设置（最多发放一万张）！</p>
            </el-form-item>
            <el-form-item prop="distributionRules.isLimitQuantity">
              <el-checkbox v-model="form.distributionRules.isLimitQuantity" :disabled="setIsLimitQuantity" @change="() => (form.distributionRules.distributionQuantity = undefined)">不限</el-checkbox>
            </el-form-item>
          </div>
        </el-form-item>

        <el-form-item prop="distributionRules.channel" label="适用渠道">
          <el-checkbox-group v-model="form.distributionRules.channel">
            <el-checkbox label="Kaka商城" name="channel" />
            <el-checkbox label="一件代发" name="channel" />
          </el-checkbox-group>
        </el-form-item>

        <el-form-item prop="receiveUsageRules.offerTypeInfo.reduction" label="优惠内容">
          <div class="reduction">
            <el-select v-model="form.receiveUsageRules.offerTypeInfo.fixedType" size="medium" :disabled="type === 3" placeholder="请选择">
              <el-option label="减价" :value="1" />
              <el-option label="折扣" :value="2" />
            </el-select>
            <el-input-number v-model="form.receiveUsageRules.offerTypeInfo.reduction" size="medium" :disabled="type === 3" controls-position="right" :placeholder="`请输入${form.receiveUsageRules.offerTypeInfo.fixedType === 1 ? '减价金额' : '折扣百分比'}`" :precision="setReductionPrecision" :min="1" :max="setReductionMax" />
            <span class="tail">
              <template v-if="form.receiveUsageRules.offerTypeInfo.fixedType === 1">USD</template>
              <template v-else>% OFF</template>
            </span>
          </div>
          <p class="tips margin-top">优惠内容是单独给客户的优惠金额，设置大额优惠金额时需谨慎，以免造成资损（测试活动建议设置小额优惠内容）！</p>
        </el-form-item>

        <el-form-item prop="receiveUsageRules.conditionsOfUse.useType" label="使用门槛">
          <el-radio-group v-model="form.receiveUsageRules.conditionsOfUse.useType" :disabled="type === 3" class="padding-top">
            <el-radio :label="1">无使用门槛</el-radio>
            <el-radio disabled :label="2">
              <span>订单满</span>
              <el-input-number v-model="form.receiveUsageRules.conditionsOfUse.amount" disabled size="mini" controls-position="right" :min="1" :max="10000" />
              <span>USD</span>
              <span>可用</span>
            </el-radio>
            <el-radio disabled :label="3">
              <span>订单满</span>
              <el-input-number v-model="form.receiveUsageRules.conditionsOfUse.clothes" disabled size="mini" controls-position="right" :min="1" :max="10000" />
              <span>件</span>
              <span>可用</span>
            </el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item prop="receiveUsageRules.overlayUseTypes" label="优惠叠加">
          <el-checkbox-group v-model="form.receiveUsageRules.overlayUseTypes" :disabled="type === 3">
            <el-checkbox label="优惠码" name="overlayUseTypes" />
          </el-checkbox-group>
        </el-form-item>

        <el-form-item label="用券时间" required>
          <el-form-item prop="distributionRules.usableTime" :required="form.distributionRules.effectiveType === 0" :rules="[{ required: form.distributionRules.effectiveType === 0, message: '请选择用券日期！', trigger: ['blur', 'change'] }]">
            <el-radio v-model="form.distributionRules.effectiveType" :label="0" :disabled="type === 3" @change="changeCouponTime">
              <el-date-picker v-model="form.distributionRules.usableTime" :disabled="type === 3" :clearable="false" :picker-options="{ disabledDate: (time) => time.getTime() < Date.now() - 8.64e7 }" type="datetimerange" :default-time="['00:00:00', '23:59:59']" value-format="timestamp" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" style="width: 100%;" @focus="onFocusDatePicker" @change="onChangeDatePicker" />
            </el-radio>
          </el-form-item>
          <el-form-item prop="distributionRules.effectivelyImmediatelyDays" :required="form.distributionRules.effectiveType === 1" style="margin-top: 10px;" :rules="[{ required: form.distributionRules.effectiveType === 1, message: '请输入用券天数！', trigger: ['blur', 'change'] }]">
            <el-radio v-model="form.distributionRules.effectiveType" :label="1" :disabled="type === 3" @change="changeCouponTime">
              <span>领券后立即生效，有效期</span>
              <el-input-number v-model="form.distributionRules.effectivelyImmediatelyDays" :disabled="type === 3" size="mini" controls-position="right" :min="1" :max="10000" style="width: 86px;margin: 0 5px;" @focus="onFocusValidityPeriodInputNumber" />
              <span>天</span>
            </el-radio>
            <p class="tips">选择允许客户领取的券也可通过系统发放，但是选择仅系统发放的券，客户不能领取！</p>
          </el-form-item>
        </el-form-item>
      </Skeleton>

      <Skeleton title="领取和使用规则">
        <el-form-item label="每人限领张数" required>
          <div class="total-amount">
            <el-form-item prop="distributionRules.quantityReceivedPerPerson" :show-message="!form.distributionRules.isLimitReceiveQuantity" :rules="[{ required: !form.distributionRules.isLimitReceiveQuantity, message: '请输入领取和使用规则！', trigger: ['blur', 'change'] }]" class="form-item">
              <el-input-number v-model="form.distributionRules.quantityReceivedPerPerson" :disabled="form.distributionRules.isLimitReceiveQuantity || type === 3" :min="1" :max="10000" controls-position="right" placeholder="请输入数量" />
            </el-form-item>
            <el-form-item prop="distributionRules.isLimitReceiveQuantity">
              <el-checkbox v-model="form.distributionRules.isLimitReceiveQuantity" :disabled="type === 3" @change="() => (form.distributionRules.quantityReceivedPerPerson = undefined)">不限</el-checkbox>
            </el-form-item>
          </div>
        </el-form-item>

        <el-form-item prop="distributionRules.receiveType" label="领取方式">
          <el-radio-group v-model="form.distributionRules.receiveType" :disabled="type === 3" class="employ">
            <el-radio :label="1">仅系统发放</el-radio>
            <el-radio disabled :label="2">允许客户领取</el-radio>
          </el-radio-group>
          <p class="tips">修改优惠券总量时只能增加不能减少，请谨慎设置！</p>
        </el-form-item>
      </Skeleton>

      <Skeleton title="适用商品">
        <el-form-item prop="receiveUsageRules.applicableProducts.applicableType" label="适用商品">
          <el-radio-group v-model="form.receiveUsageRules.applicableProducts.applicableType" class="employ">
            <el-radio :label="1">全部商品可用</el-radio>
            <el-radio disabled :label="2">非活动商品可用</el-radio>
            <el-radio disabled :label="3">指定商品可用</el-radio>
          </el-radio-group>
        </el-form-item>
      </Skeleton>

      <Skeleton title="更多设置" v-if="false">更多设置</Skeleton>
    </el-form>

    <div class="footer">
      <el-button :disabled="uploadImgLoading || loading" size="medium" @click="onCancel">取 消</el-button>
      <el-button v-if="[1, 4].includes(type)" :disabled="uploadImgLoading || loading" size="medium" type="info" @click="onReset">重 置</el-button>
      <el-button v-if="[1, 3, 4].includes(type)" :disabled="uploadImgLoading" :loading="loading" size="medium" type="primary" @click="onSubmit">提 交</el-button>
    </div>
  </div>
</template>

<script>
import API from '@/api'
import Skeleton from './Skeleton.vue'
import cloneDeep from 'lodash/cloneDeep'
import CouponItem from '@/components/CouponItem.vue'
import { validateCouponName, validateCouponDescribe } from './validate.js'

export default {
  components: {
    Skeleton,
    CouponItem
  },
  data() {
    return {
      validateCouponName, // 验证优惠券名称：只能填字母、数字、空格、下划线
      validateCouponDescribe, // 验证优惠券描述：只能填中文、字母、数字、空格、下划线

      labelWidth: 108,

      type: 1, // 当前操作的类型（默认新增）

      uploadImgLoading: false, // 上传图片时的加载效果

      // 优惠券预览对象
      coupon: {
        id: 1, // 优惠券ID
        selected: false, // 是否选择使用该优惠券（发起请求前需要删除该字段！）
        couponTicketName: undefined, // 优惠券名称
        usableStartTime: 1679129046000, // 可用开始时间
        usableEndTime: 1684399446000, // 可用结束时间
        status: 0, // 客户优惠券状态：0.可用 1.已使用 2.已过期 3.已作废
        logoUrl: undefined, // 优惠券图标
        // 领取使用规则
        receiveUsageRules: {
          // 使用条件（门槛）
          overlayUseTypes: [], // 优惠叠加
          conditionsOfUse: {
            useType: 2, // 使用类型：1.无使用门槛 2.订单满多少钱减 3.订单满多少件减
            threshold: 168 // 阀值
          },
          // 适用的商品
          applicableProducts: {
            applicableType: 1 // 适用商品类型：1.全部商品可用 2.非全部活动商品可用 3.指定商品
          },
          // 优惠的内容
          offerTypeInfo: {
            fixedType: 1, // 优惠内容类型：1.固定减价 2.固定折扣
            reduction: 98 // 减少量
          }
        }
      },

      form: {
        id: undefined, // 优惠券ID（编辑时传）
        couponTicketName: undefined, // 优惠券名称
        description: undefined, // 优惠券描述
        logoUrl: undefined, // 优惠券图片链接
        // 基本设置
        distributionRules: {
          isLimitQuantity: false, // 是否限制发放数量：true-限制 false-不限制
          distributionQuantity: undefined, // 发放数量
          channel: ['Kaka商城'], // 适用渠道：Kaka商城、一件代发

          quantityReceivedPerPerson: undefined, // 领取和使用规则 - 每人限领张数
          isLimitReceiveQuantity: false, // 领取和使用规则 - 不限：true.限制 false.不限制

          receiveType: 1, // 领取和使用规则 - 领取方式：1.仅系统发放 2.允许客户领取
          effectiveType: 0, // 用券时间 - 单选框：0.固定时间 1.立即生效
          usableTime: [], // 用券时间 - 时间日期选择器双向绑定的值（发送请求前需要删除该字段！）
          usableStartTime: undefined, // 用券时间 - 有效期开始时间
          usableEndTime: undefined, // 用券时间 - 有效期开始时间
          effectivelyImmediatelyDays: undefined // 用券时间 - 领券后立即生效的有效天数
        },
        // 使用门槛
        receiveUsageRules: {
          conditionsOfUse: {
            useType: 1, // 使用类型：1.无使用门槛 2.订单满多少减 3.订单满多少件减
            threshold: undefined, // 阀值 - 需要根据选择的类型取出对应输入框的值赋值到此字段中
            amount: undefined, // 订单满多少减 - 输入框的值（发送请求前需要删除该字段！）
            clothes: undefined // 订单满多少件减 - 输入框的值（发送请求前需要删除该字段！）
          },
          overlayUseTypes: [], // 优惠叠加：优惠码
          // 适用商品
          applicableProducts: {
            applicableType: 1 //  适用商品类型：1.全部商品可用 2.非活动商品可用 3.指定商品可用
          },
          // 优惠内容
          offerTypeInfo: {
            fixedType: 1, // 优惠内容 - 类型：1.减价 2.折扣
            reduction: undefined // 减少量 - 输入框的值
          }
        }
      },

      rules: {
        couponTicketName: [{ required: true, validator: validateCouponName, trigger: ['blur', 'change'] }],
        description: [{ required: false, validator: validateCouponDescribe, trigger: ['blur', 'change'] }],
        'distributionRules.channel': [{ type: 'array', required: true, message: '请至少选择一个适用渠道', trigger: 'change' }],
        'receiveUsageRules.offerTypeInfo.reduction': [{ required: true, message: '请输入优惠内容！', trigger: ['blur', 'change'] }],
        'receiveUsageRules.conditionsOfUse.useType': [{ required: true, message: '请选择使用门槛！', trigger: 'change' }],
        'distributionRules.receiveType': [{ required: true, message: '请输入每人限领张数！', trigger: 'change' }],
        'receiveUsageRules.applicableProducts.applicableType': [{ required: true, message: '请输选择适用商品类型！', trigger: 'change' }]
      },

      loading: false, // 提交按钮的加载效果

      isLimitQuantity: false, // 是否限制发放数量：true-限制 false-不限制（编辑操作时：如果是已勾选的不限数量，则数字输入框以及复选框禁用）
      distributionQuantity: undefined // 发放数量（编辑操作时：发放总量只能增加不能减少，此字段用于保存数量并限制输入框输入的值）
    }
  },
  computed: {
    setReductionMax() {
      if (this.form.receiveUsageRules.offerTypeInfo.fixedType === 1) {
        return 100000
      } else {
        return 99
      }
    },
    setReductionPrecision() {
      if (this.form.receiveUsageRules.offerTypeInfo.fixedType === 1) {
        return 2
      } else {
        return 0
      }
    },
    setIsLimitQuantity() {
      return this.type === 3 && this.isLimitQuantity ? true : false
    },
    setDistributionQuantity() {
      return this.type === 3 ? this.distributionQuantity : 1
    },
    defaultTips() {
      return (value) => value || '-'
    }
  },
  created() {
    if (this.$store.state.coupon && this.$store.state.coupon.couponInfo) {
      this.type = this.$store.state.coupon.couponInfo.operateType // 操作类型：1.创建优惠券 2.查看优惠券 3.编辑优惠券 4.复制优惠券
      if ([2, 3, 4].includes(this.type)) {
        let id = this.$store.state.coupon.couponInfo.couponId // 当前操作的优惠券ID
        this.getCouponDetails(id, this.type)
      }
    } else {
      this.onCancel()
    }
  },
  methods: {
    /**
     * API 新增或编辑优惠券
     */
    async setOrEditCoupon() {
      try {
        this.loading = true
        let form = cloneDeep(this.form)
        if (form.receiveUsageRules.conditionsOfUse.useType === 2) {
          form.receiveUsageRules.conditionsOfUse.threshold = form.receiveUsageRules.conditionsOfUse.amount
        } else if (form.receiveUsageRules.conditionsOfUse.useType === 3) {
          form.receiveUsageRules.conditionsOfUse.threshold = form.receiveUsageRules.conditionsOfUse.clothes
        } else {
          form.receiveUsageRules.conditionsOfUse.threshold = undefined
        }

        // 后端要求在勾选的时候取反（即勾选状态传false，未勾选状态传true）
        form.distributionRules.isLimitQuantity = !form.distributionRules.isLimitQuantity
        form.distributionRules.isLimitReceiveQuantity = !form.distributionRules.isLimitReceiveQuantity

        delete form.distributionRules.usableTime // 用券时间 - 时间日期选择器双向绑定的值（发送请求前需要删除该字段！）
        delete form.receiveUsageRules.conditionsOfUse.amount // 订单满多少减 - 输入框的值（发送请求前需要删除该字段！）
        delete form.receiveUsageRules.conditionsOfUse.clothes // 订单满多少件减 - 输入框的值（发送请求前需要删除该字段！）
        let { code, message } = await API.setOrEditCoupon(form)
        if (code === 10000) {
          if (this.type === 3) {
            this.onCancel()
            this.$message.success(message)
          } else {
            this.$confirm('优惠券新增成功, 继续新增?', '提示', {
              confirmButtonText: '继续',
              cancelButtonText: '返回',
              type: 'success',
              center: true
            })
              .then(() => {
                this.onReset()
                this.$message.success(message)
              })
              .catch(() => {
                this.onCancel()
              })
          }
        } else {
          this.$message.error(message)
        }
        this.loading = false
      } catch (error) {
        this.loading = false
      }
    },

    /**
     * API 上传文件
     *
     * @param {Array} files
     */
    async uploadImages(files) {
      this.uploadImgLoading = true
      API.signValue({ directory: 'test/' })
        .then(({ data }) => {
          let formData = new FormData()
          formData.append('directory', 'test/')
          formData.append('signValue', data)
          files.forEach((file) => {
            formData.append('fileList', file)
          })
          API.uploadFile(formData)
            .then(({ data, code, message }) => {
              if (code === 10000) {
                let result = data.split(',').length ? data.split(',') : []
                this.form.logoUrl = result[0]
                this.coupon.logoUrl = result[0]
              } else {
                this.$message.error(message)
              }
              this.uploadImgLoading = false
            })
            .catch((error) => {
              this.uploadImgLoading = false
              this.$message.error(error.message)
            })
        })
        .catch((error) => {
          this.uploadImgLoading = false
          this.$message.error(error.message)
        })
    },

    /**
     * API 获取优惠券详情
     *
     * @param {Number} id 优惠券ID
     * @param {Number} type 当前操作的类型
     */
    async getCouponDetails(id, type) {
      let { data: details } = await API.getCouponDetails({ id })
      if (details && details instanceof Object) {
        this.form.id = details.id // 优惠券ID（编辑时传）
        this.form.couponTicketName = details.couponTicketName // 优惠券名称
        this.form.description = details.description // 优惠券描述
        this.form.logoUrl = details.logoUrl // 优惠券图片链接
        // 基本设置
        this.form.distributionRules.isLimitQuantity = details.distributionRules.isLimitQuantity // 是否限制发放数量：true-限制 false-不限制
        this.form.distributionRules.distributionQuantity = details.distributionRules.distributionQuantity // 发放数量
        this.form.distributionRules.channel = details.distributionRules.channel // 适用渠道：Kaka商城、一件代发
        this.form.distributionRules.quantityReceivedPerPerson = details.distributionRules.quantityReceivedPerPerson // 领取和使用规则 - 每人限领张数
        this.form.distributionRules.isLimitReceiveQuantity = details.distributionRules.isLimitReceiveQuantity // 领取和使用规则 - 不限：true.限制 false.不限制
        this.form.distributionRules.receiveType = details.distributionRules.receiveType // 领取和使用规则 - 领取方式：1.仅系统发放 2.允许客户领取
        this.form.distributionRules.effectiveType = details.distributionRules.effectiveType // 用券时间 - 单选框：0.固定时间 1.立即生效
        this.form.distributionRules.usableTime = [] // 用券时间 - 时间日期选择器双向绑定的值（发送请求前需要删除该字段！）
        this.form.distributionRules.usableStartTime = details.distributionRules.usableStartTime // 用券时间 - 有效期开始时间
        this.form.distributionRules.usableEndTime = details.distributionRules.usableEndTime // 用券时间 - 有效期开始时间
        this.form.distributionRules.effectivelyImmediatelyDays = details.distributionRules.effectivelyImmediatelyDays // 用券时间 - 领券后立即生效的有效天数

        // 使用门槛
        this.form.receiveUsageRules.conditionsOfUse.useType = details.receiveUsageRules.conditionsOfUse.useType // 使用类型：1.无使用门槛 2.订单满多少减 3.订单满多少件减
        this.form.receiveUsageRules.conditionsOfUse.threshold = details.receiveUsageRules.conditionsOfUse.threshold // 阀值 - 需要根据选择的类型取出对应输入框的值赋值到此字段中
        this.form.receiveUsageRules.conditionsOfUse.amount = undefined // 订单满多少减 - 输入框的值（发送请求前需要删除该字段！）
        this.form.receiveUsageRules.conditionsOfUse.clothes = undefined // 订单满多少件减 - 输入框的值（发送请求前需要删除该字段！）
        this.form.receiveUsageRules.overlayUseTypes = details.receiveUsageRules.overlayUseTypes // 优惠叠加：优惠码
        this.form.receiveUsageRules.applicableProducts.applicableType = details.receiveUsageRules.applicableProducts.applicableType // 适用商品类型：1.全部商品可用 2.非活动商品可用 3.指定商品可用
        this.form.receiveUsageRules.offerTypeInfo.fixedType = details.receiveUsageRules.offerTypeInfo.fixedType // 优惠内容 - 类型：1.减价 2.折扣
        this.form.receiveUsageRules.offerTypeInfo.reduction = details.receiveUsageRules.offerTypeInfo.reduction // 减少量 - 输入框的值

        this.coupon.logoUrl = details.logoUrl // 优惠券预览对象 - 优惠券图标

        if (this.form.distributionRules.usableStartTime && this.form.distributionRules.usableEndTime) {
          this.form.distributionRules.usableTime = [this.form.distributionRules.usableStartTime, this.form.distributionRules.usableEndTime]
        }
        // 使用门槛
        if (this.form.receiveUsageRules.conditionsOfUse.useType === 2) {
          // 订单满多少减
          this.form.receiveUsageRules.conditionsOfUse.amount = this.form.receiveUsageRules.conditionsOfUse.threshold
        } else if (this.form.receiveUsageRules.conditionsOfUse.useType === 3) {
          // 订单满多少件减
          this.form.receiveUsageRules.conditionsOfUse.clothes = this.form.receiveUsageRules.conditionsOfUse.threshold
        }
        this.form.receiveUsageRules.conditionsOfUse.threshold = undefined

        // 后端要求在勾选的时候取反（即勾选状态传false，未勾选状态传true）
        this.form.distributionRules.isLimitQuantity = !this.form.distributionRules.isLimitQuantity
        this.form.distributionRules.isLimitReceiveQuantity = !this.form.distributionRules.isLimitReceiveQuantity
      }

      switch (type) {
        case 3: // 编辑
          this.isLimitQuantity = this.form.distributionRules.isLimitQuantity
          this.distributionQuantity = this.form.distributionRules.distributionQuantity
          break
        case 4: // 复制
          this.form.id = undefined
          this.isLimitQuantity = false
          this.distributionQuantity = undefined
          // 用券时间
          if (this.form.distributionRules.effectiveType === 0) {
            this.form.distributionRules.usableTime = []
            this.form.distributionRules.usableStartTime = undefined
            this.form.distributionRules.usableEndTime = undefined
          }
          break
        default:
          this.isLimitQuantity = false
          this.distributionQuantity = undefined
          break
      }
    },

    /**
     * 优惠券图片：上传文件时的钩子
     *
     * @param {Blob} file 当前上传的文件
     * @param {Boolean} 返回false则阻止默认上传行为
     */
    beforeCouponImgUpload(file) {
      if (file) {
        this.uploadImages([file])
      } else {
        this.$message.error('Failed to upload image!')
      }
      return false
    },

    /**
     * 点击删除已上传优惠券图片的触发的事件
     */
    handleRemoveCouponImgIcon() {
      this.form.logoUrl = undefined
      this.coupon.logoUrl = undefined
    },

    /**
     * 用券时间 - 单选按钮触发的回调事件
     *
     * @param {Number} value 单选框绑定的值
     */
    changeCouponTime(value) {
      if (value === 0) {
        this.form.distributionRules.effectivelyImmediatelyDays = undefined
        this.$refs.form.validateField(['distributionRules.usableTime'])
        this.$refs.form.clearValidate(['distributionRules.effectivelyImmediatelyDays'])
      }
      if (value === 1) {
        this.form.distributionRules.usableTime = undefined
        this.$refs.form.validateField(['distributionRules.effectivelyImmediatelyDays'])
        this.$refs.form.clearValidate(['distributionRules.usableTime'])
      }
    },

    /**
     * 用券时间 - 日期选择器改变时的事件
     *
     * @param {Array} date 当前选中的开始时间戳和结束时间戳
     */
    onChangeDatePicker(date) {
      if (date && date.length) {
        this.form.distributionRules.usableStartTime = date[0] // 开始时间
        this.form.distributionRules.usableEndTime = date[1] // 结束时间
      } else {
        this.form.distributionRules.usableStartTime = undefined
        this.form.distributionRules.usableEndTime = undefined
      }
    },

    /**
     * 用券时间 - 日期选择器获取焦点时的事件
     */
    onFocusDatePicker() {
      this.form.distributionRules.effectiveType = 0
      this.form.distributionRules.effectivelyImmediatelyDays = undefined
      this.$refs.form.clearValidate(['distributionRules.effectivelyImmediatelyDays'])
    },

    /**
     * 用券时间 - 数字输入框获取焦点时的事件
     */
    onFocusValidityPeriodInputNumber() {
      this.form.distributionRules.effectiveType = 1
      this.form.distributionRules.usableTime = undefined
      this.$refs.form.clearValidate(['distributionRules.usableTime'])
    },

    /**
     * 取消
     */
    onCancel() {
      this.$router.replace('/operation/coupon')
      this.$store.dispatch('coupon/removeCouponInfo')
      this.onReset()
    },

    /**
     * 重置
     */
    onReset() {
      this.$refs.form.resetFields()
      this.form.distributionRules.effectiveType = 0 // 用券时间单选框
      this.form.distributionRules.usableEndTime = undefined // 用券时间结束时间
      this.form.distributionRules.usableStartTime = undefined // 用券时间开始时间
    },

    /**
     * 提交
     */
    onSubmit() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          if (this.type === 3) {
            this.$confirm('此操作将修改当前优惠券的规则, 是否继续?', '提示', {
              confirmButtonText: '确认',
              cancelButtonText: '放弃',
              type: 'warning'
            })
              .then(() => {
                this.setOrEditCoupon()
              })
              .catch(() => {
                this.$message.info('已放弃修改')
              })
          } else {
            this.setOrEditCoupon()
          }
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-info {
  position: relative;
  max-width: 1200px;
  min-width: 1000px;
  height: 100%;
  padding-bottom: 56px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 4px;

  .el-form {
    height: 100%;
    padding: 12px;
    overflow: auto;
    .coupon-id {
      color: #aaa;
    }

    .el-input,
    .el-input-number {
      width: 50%;
    }

    .el-input-number {
      /deep/.el-input__inner {
        text-align: start;
      }
    }

    .total-amount {
      display: flex;
      .form-item {
        width: 50%;
      }
      .el-input-number {
        width: 100%;
      }
      .el-checkbox {
        margin-left: 12px;
      }
    }

    .tips {
      color: #aaa;
      font-size: 12px;
      line-height: normal;
      &.margin-top {
        margin-top: 5px;
      }
    }

    .reduction {
      .el-select {
        width: 80px;
        /deep/.el-input__inner {
          border-right: none;
          background-color: #f5f7fa;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      }
      .el-input-number {
        width: calc(50% - 150px);
        /deep/.el-input__inner {
          border-radius: 0;
        }
      }
      .tail {
        display: inline-block;
        color: #909399;
        width: 70px;
        line-height: 34px;
        text-align: center;
        border: 1px solid #dcdfe6;
        border-left: none;
        background-color: #f5f7fa;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }
    }

    .coupon-uploader {
      display: flex;
      width: 50%;
      align-items: flex-end;
      /deep/.el-upload {
        display: block;
        position: relative;
        width: 88px;
        height: 88px;
        border: 1px dashed #d9d9d9;
        overflow: hidden;
        cursor: pointer;
        border-radius: 6px;
        &:hover {
          border-color: #99c41c;
        }
        .coupon-img {
          position: relative;
          width: 100%;
          height: 100%;
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .delete-icon {
            position: absolute;
            left: 0;
            bottom: 4px;
            width: 100%;
            color: #fff;
            line-height: 24px;
            background-color: #000;
            opacity: 0.4;
            cursor: pointer;
            &:hover {
              color: #f56c6c;
            }
          }
        }
        .coupon-uploader-tips {
          display: flex;
          color: #aaa;
          height: 100%;
          text-align: center;
          flex-direction: column;
          justify-content: center;
          .text {
            font-size: 12px;
            line-height: 26px;
          }
        }
      }
      .size {
        color: #aaa;
        font-size: 12px;
        margin-left: 12px;
        line-height: 16px;
      }
      .popper {
        line-height: 16px;
      }
      .preview {
        color: #99c41c;
        cursor: pointer;
        user-select: none;
      }
    }

    .el-radio-group {
      &.padding-top {
        padding-top: 10px;
      }
      &.employ {
        .el-radio {
          display: inline;
          margin-right: 20px;
        }
      }
      .el-radio {
        display: block;
        margin-right: 0;
        margin-bottom: 10px;
        &:last-child {
          margin-bottom: 0;
        }
      }
      .el-input-number {
        width: 86px;
        margin: 0 5px;
      }
    }
  }

  .footer {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    padding: 10px 0;
    text-align: center;
    background-color: #fff;
    box-shadow: 0px -1px 12px 0 rgba(0, 0, 0, 0.1);
  }
}
</style>
